/**
 * MemoSimulator
 * Provides instant client-side simulation of video edits without rendering
 * - Skip-range playback (jumps over deleted segments)
 * - Modified caption injection via text tracks
 * - Effective duration calculation
 */

export interface CutSegment {
  start: number;  // milliseconds
  end: number;    // milliseconds
  word?: string;  // optional word identifier
}

export interface CaptionEdit {
  findText: string;
  replaceWith: string;
}

export interface EditPlan {
  cuts: CutSegment[];
  captions: CaptionEdit[];
  audio: any | null;
}

export class MemoSimulator {
  private cutSegments: CutSegment[];
  private originalDuration: number;
  private skipCheckInterval: number = 100; // Check every 100ms

  constructor(
    private startTime: number,  // seconds
    private endTime: number,    // seconds
    private editPlan: EditPlan
  ) {
    this.cutSegments = editPlan.cuts || [];
    this.originalDuration = endTime - startTime;
  }

  /**
   * Check if current playback time is in a cut segment
   * If so, return the time to skip to
   */
  shouldSkip(currentTime: number): number | null {
    const currentMs = currentTime * 1000;

    for (const cut of this.cutSegments) {
      // Check if we're currently in a cut segment
      if (currentMs >= cut.start && currentMs < cut.end) {
        // Skip to end of cut segment (convert back to seconds)
        return cut.end / 1000 + 0.01; // Add 10ms buffer
      }
    }

    return null;
  }

  /**
   * Install skip-range listener on video element
   * Returns cleanup function
   */
  installSkipListener(videoElement: HTMLVideoElement): () => void {
    const checkInterval = setInterval(() => {
      if (!videoElement || videoElement.paused) return;

      const skipTo = this.shouldSkip(videoElement.currentTime);
      if (skipTo !== null) {
        console.log(`[MemoSimulator] Skipping from ${videoElement.currentTime}s to ${skipTo}s`);
        videoElement.currentTime = skipTo;
      }
    }, this.skipCheckInterval);

    return () => clearInterval(checkInterval);
  }

  /**
   * Generate modified VTT content with caption edits applied
   */
  applyVTTModifications(originalVTT: string): string {
    if (!this.editPlan.captions || this.editPlan.captions.length === 0) {
      return originalVTT;
    }

    const { VTTProcessor } = require('../video-processing/vtt-processor');

    return VTTProcessor.modifyVTTText(
      originalVTT,
      this.editPlan.captions
    );
  }

  /**
   * Create blob URL for modified VTT (for text track injection)
   */
  createVTTBlobURL(originalVTT: string): string {
    const modifiedVTT = this.applyVTTModifications(originalVTT);
    const blob = new Blob([modifiedVTT], { type: 'text/vtt' });
    return URL.createObjectURL(blob);
  }

  /**
   * Calculate effective duration after cuts
   */
  getEffectiveDuration(): number {
    const cutDuration = this.cutSegments.reduce(
      (sum, cut) => sum + (cut.end - cut.start),
      0
    ) / 1000; // Convert ms to seconds

    return this.originalDuration - cutDuration;
  }

  /**
   * Get cut segments for timeline visualization
   * Returns segments in seconds (not milliseconds)
   */
  getCutSegmentsForTimeline(): Array<{start: number, end: number, duration: number}> {
    return this.cutSegments.map(cut => ({
      start: cut.start / 1000,
      end: cut.end / 1000,
      duration: (cut.end - cut.start) / 1000
    }));
  }

  /**
   * Check if a specific time is within a cut segment
   */
  isTimeCut(timeSeconds: number): boolean {
    const timeMs = timeSeconds * 1000;
    return this.cutSegments.some(cut =>
      timeMs >= cut.start && timeMs <= cut.end
    );
  }

  /**
   * Map original timestamp to effective timestamp (after cuts)
   * Used for transcript word positioning
   */
  mapToEffectiveTime(originalTimeSeconds: number): number {
    const originalMs = originalTimeSeconds * 1000;
    let offset = 0;

    // Calculate how much time was cut before this timestamp
    for (const cut of this.cutSegments) {
      if (cut.end <= originalMs) {
        offset += (cut.end - cut.start);
      } else if (cut.start < originalMs && cut.end > originalMs) {
        // Timestamp is in the middle of a cut (shouldn't happen for valid words)
        offset += (originalMs - cut.start);
        break;
      }
    }

    return (originalMs - offset) / 1000;
  }
}
